"use client"

import { useState, memo, useCallback, useMemo } from "react"
import type { TowerType } from "@/types/tower.types"
import { towerTypes } from "@/data/tower-types"
import { LockIcon, LoaderIcon, CheckIcon } from "@/components/icons/pixel-icons"
import { useAuth } from "@/hooks/use-auth"
import { RadialMenu, type RadialMenuOption } from "@/components/ui/radial-menu"
import { TooltipProvider } from "@/components/ui/tooltip"

interface RadialTowerMenuProps {
  position: { x: number; y: number }
  money: number
  onSelectTower: (type: TowerType) => void
  onClose: () => void
  onProcessingPayment?: (isProcessing: boolean) => void
  freePremiumTowers?: number
  isMobile?: boolean
}

const getAttackSpeedLabel = (attackSpeed: number): string => {
  if (attackSpeed <= 30) return "Very Fast"
  if (attackSpeed <= 50) return "Fast"
  if (attackSpeed <= 80) return "Average"
  if (attackSpeed <= 120) return "Slow"
  return "Very Slow"
}

// Tower visual configuration - defined once at module load
const TOWER_UI_CONFIG: Record<TowerType, {
  iconPath: string
  iconStyle: React.CSSProperties
  colors: { bg: string; border: string }
  titleColor: string
}> = {
  archer: {
    iconPath: "/images/towers/archer/icon.png",
    iconStyle: {
      imageRendering: "pixelated",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.5))",
      marginTop: "5px",
    },
    colors: { bg: "#c8e6c9", border: "#81c784" },
    titleColor: "#9ca3af",
  },
  mage: {
    iconPath: "/images/towers/mage/icon.png",
    iconStyle: {
      imageRendering: "pixelated",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.5))",
      marginTop: "5px",
    },
    colors: { bg: "#e1bee7", border: "#ba68c8" },
    titleColor: "#a855f7",
  },
  siege: {
    iconPath: "/images/towers/siege/icon.png",
    iconStyle: {
      imageRendering: "pixelated",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.5))",
      marginTop: "1px",
      transform: "scale(0.9)",
    },
    colors: { bg: "#ffe0b2", border: "#ffb74d" },
    titleColor: "#f59e0b",
  },
  barracks: {
    iconPath: "/images/towers/barracks/2 Idle/1.png",
    iconStyle: {
      imageRendering: "pixelated",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.5))",
      marginTop: "-18px",
    },
    colors: { bg: "#bbdefb", border: "#64b5f6" },
    titleColor: "#3b82f6",
  },
  shrine: {
    iconPath: "/images/towers/shrine/shrine_1_object.png",
    iconStyle: {
      imageRendering: "pixelated",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.5))",
    },
    colors: { bg: "#c7d2fe", border: "#818cf8" },
    titleColor: "#818cf8",
  },
  shadowblade: {
    iconPath: "", // Falls back to colored circle
    iconStyle: {},
    colors: { bg: "#fde68a", border: "#fbbf24" },
    titleColor: "#f59e0b",
  },
}

export const RadialTowerMenu = memo(function RadialTowerMenu({
  position,
  money,
  onSelectTower,
  onClose,
  onProcessingPayment,
  freePremiumTowers = 0,
  isMobile = false,
}: RadialTowerMenuProps) {
  const { identityKey, connect } = useAuth()
  const [processingTower, setProcessingTower] = useState<TowerType | null>(null)

  const towerOrder: TowerType[] = ["archer", "mage", "siege", "barracks", "shadowblade", "shrine"]

  const handleTowerClick = useCallback(async (towerType: TowerType) => {
    const tower = towerTypes[towerType]

    if (tower.isPremium && freePremiumTowers > 0) {
      onSelectTower(towerType)
      onClose()
      return
    }

    if (tower.isPremium && !identityKey) {
      // No account yet - open the BRC-100 wallet connect flow directly
      connect()
      return
    }

    if (tower.isPremium) {
      setProcessingTower(towerType)
      if (onProcessingPayment) {
        onProcessingPayment(true)
      }
    }

    onSelectTower(towerType)

    if (!tower.isPremium) {
      onClose()
    }
  }, [freePremiumTowers, identityKey, connect, onSelectTower, onClose, onProcessingPayment])

  const getTowerIcon = useCallback((towerType: TowerType, isProcessing: boolean): React.ReactNode => {
    if (isProcessing) {
      return (
        <LoaderIcon
          className="text-foreground"
          size={32}
          style={{ filter: "drop-shadow(0 0 3px black)" }}
        />
      )
    }

    const config = TOWER_UI_CONFIG[towerType]

    if (config.iconPath) {
      return (
        <img
          src={config.iconPath}
          alt={towerTypes[towerType].name}
          className={towerType === "shrine" ? "w-10 h-10" : "w-12 h-12"}
          style={config.iconStyle}
        />
      )
    }

    // Fallback for shadowblade
    return (
      <div
        className="w-8 h-8 rounded-full border-2 border-game-surface shadow-md"
        style={{ backgroundColor: towerTypes[towerType].baseColor }}
      />
    )
  }, [])

  const getTowerBadge = useCallback((towerType: TowerType): React.ReactNode => {
    const tower = towerTypes[towerType]
    const hasFreeToken = tower.isPremium && freePremiumTowers > 0
    const isLocked = tower.isPremium && !identityKey && !hasFreeToken

    if (isLocked) {
      return (
        <div className="bg-muted rounded-full p-1">
          <LockIcon className="text-muted-foreground" size={12} />
        </div>
      )
    }

    if (hasFreeToken) {
      return (
        <div className="bg-game-success rounded-full p-1">
          <CheckIcon className="text-foreground" size={12} />
        </div>
      )
    }

    return null
  }, [freePremiumTowers, identityKey])

  const getTowerSublabel = useCallback((towerType: TowerType): React.ReactNode => {
    const tower = towerTypes[towerType]
    const hasFreeToken = tower.isPremium && freePremiumTowers > 0

    if (hasFreeToken) {
      return (
        <span
          className="text-game-success font-bold text-xs leading-none"
          style={{ textShadow: "0 0 3px black, 0 0 3px black" }}
        >
          FREE
        </span>
      )
    }

    if (tower.isPremium) {
      return (
        <span
          className="text-game-success text-xs font-bold leading-none inline-flex items-center gap-0.5"
          style={{ textShadow: "0 0 3px black, 0 0 3px black" }}
        >
          <img src="/images/ui/crystal.svg" alt="" width={10} height={10} />
          {tower.premiumCostCrystals}
        </span>
      )
    }

    return null
  }, [freePremiumTowers])

  const options: RadialMenuOption[] = useMemo(() => {
    return towerOrder.map((towerType) => {
      const tower = towerTypes[towerType]
      const cost = tower.upgrades[0].cost
      const canAfford = money >= cost
      const isPremium = tower.isPremium
      const hasFreeToken = isPremium && freePremiumTowers > 0
      const isLocked = isPremium && !identityKey && !hasFreeToken
      const isProcessing = processingTower === towerType
      const level1Stats = tower.upgrades[0]
      const config = TOWER_UI_CONFIG[towerType]

      return {
        id: towerType,
        icon: getTowerIcon(towerType, isProcessing),
        label: hasFreeToken ? undefined : String(cost),
        sublabel: getTowerSublabel(towerType),
        disabled: (!canAfford && !isLocked && !hasFreeToken) || isProcessing,
        badge: getTowerBadge(towerType),
        onClick: () => handleTowerClick(towerType),
        backgroundColor: config.colors.bg,
        borderColor: config.colors.border,
        tooltip: {
          title: tower.name,
          description: tower.description,
          titleColor: config.titleColor,
          stats: level1Stats.damage > 0 ? {
            damage: String(level1Stats.damage),
            attackSpeed: getAttackSpeedLabel(level1Stats.attackSpeed),
          } : undefined,
        },
      }
    })
  }, [
    money,
    freePremiumTowers,
    identityKey,
    processingTower,
    getTowerIcon,
    getTowerBadge,
    getTowerSublabel,
    handleTowerClick,
  ])

  return (
    <TooltipProvider delayDuration={200}>
      <div id="tower-menu">
        <RadialMenu
          position={position}
          options={options}
          radius={70}
          iconSize={52}
          onClose={onClose}
          isMobile={isMobile}
        />
      </div>
    </TooltipProvider>
  )
})
